import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { estafetaAxios } from "@/utils/estafetaAxios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;
const ESTAFETA_BUILD_ID = "estafeta-diagnostics-2026-06-26-v2";
let _hookInstanceCount = 0;

export default function useEstafetaDashboard({ initialDeliverer, token, onLogout, t }) {
  // Single-mount guard — confirms the hook is not instantiated more than once
  useEffect(() => {
    _hookInstanceCount += 1;
    console.log("[ESTAFETA_HOOK_MOUNTED] build_id=%s instance=%d", ESTAFETA_BUILD_ID, _hookInstanceCount);
    if (_hookInstanceCount > 1) console.error("[ESTAFETA_HOOK_DOUBLE_MOUNT] WARNING: hook mounted %d times", _hookInstanceCount);
    return () => { _hookInstanceCount -= 1; };
  }, []);

  const [deliverer, setDeliverer] = useState(initialDeliverer);
  const [assigned, setAssigned] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const gpsRef = useRef(null);
  const prevCountRef = useRef(0);
  const firstLoadDoneRef = useRef(false);
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [gpsPermission, setGpsPermission] = useState(null); // "granted" | "denied" | "prompt" | null
  const [lastLocationSent, setLastLocationSent] = useState(null); // Date.now() on last successful POST
  const audioRef = useRef(null);

  // Initialise notification audio and request permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    const audio = new Audio(
      "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdYeUo5Z3Z2N3jZ+lorWci3hxe4aSm5CGeXp/hpOflpiLeX6Ck5mOh4Z/f4CIkpSPg315eICHj5aViXt2d3yEjJSVi3p0c3uEjZSPh4R+f4SGipCPioh/ent+hYqPjYmHgoB+gYWIioyLiIaCgYGDhomKiYiGhIOCgoWHiYqKiYeFgoGChIaJiomIhoSDgoKEh4mKiomGhIOChIaIiYmIh4WEg4OFh4iJiYmHhoSEhIWGiIiIiIeGhYSEhYaHiIiIh4aFhYWFhoaHh4eHhoaFhYaGhoeHh4eHhoWFhYaGh4eHhw=="
    );
    audioRef.current = audio;
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    } catch {}
  }, []);

  const sendBrowserNotification = useCallback((title, body) => {
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification(title, { body, tag: "estafeta-order" });
      } catch {}
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const ordersUrl = `${API}/api/v1/deliverers/app/orders`;
      console.log("[ESTAFETA_FETCH_START] url=", ordersUrl, "api_base=", API);
      console.log("[ESTAFETA_AXIOS_URL]", API);
      console.log("[ESTAFETA_AXIOS_TOKEN]", token ? `prefix=${token.slice(0, 8)} len=${token.length}` : "null/undefined — SEM TOKEN");
      const res = await estafetaAxios(token).get(ordersUrl);
      const newAssigned = res.data.assigned || [];
      const newCompleted = res.data.completed || [];

      console.log("[FRONT_FETCH_RAW]", res.data);
      console.log("[ESTAFETA_FRONT_FINAL_RESPONSE]", {
        build_id: ESTAFETA_BUILD_ID,
        url: ordersUrl,
        api_base: API,
        raw_response: res.data,
        assigned_count: newAssigned.length,
        assigned_statuses: newAssigned.map(o => o.status),
      });
      console.log("[ESTAFETA_FRONT_RESPONSE_RAW]", JSON.stringify({ assigned: newAssigned.map(o => ({ id: o.id?.slice(-6), status: o.status, deliverer_id: o.deliverer_id?.slice(-6) })), completed: newCompleted.length, _debug: res.data._debug }));
      console.log("[ESTAFETA_FRONT_ASSIGNED_COUNT]", newAssigned.length);

      if (firstLoadDoneRef.current && newAssigned.length > prevCountRef.current) {
        const diff = newAssigned.length - prevCountRef.current;
        playNotificationSound();
        sendBrowserNotification(t("newOrder"), `${diff} ${t("newOrdersMsg")}`);
        toast.success(`${diff} ${t("newOrdersToast")}`, { duration: 5000 });
      }
      prevCountRef.current = newAssigned.length;

      setAssigned(newAssigned);
      console.log("[FRONT_AFTER_SETASSIGNED]", newAssigned.length);
      console.log("[ESTAFETA_STATE_SET]", {
        build_id: ESTAFETA_BUILD_ID,
        assigned_count: newAssigned.length,
        assigned_statuses: newAssigned.map(o => o.status),
      });
      setCompleted(newCompleted);
      setDeliverer(prev => res.data.deliverer || prev);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      console.error("[FRONT_FETCH_ERROR] status=%s msg=%s", err.response?.status ?? "network", err.message, err);
    }
    setLoading(false);
    firstLoadDoneRef.current = true;
  }, [token, onLogout, playNotificationSound, sendBrowserNotification, t]);

  useEffect(() => {
    console.log("[FRONT_RENDER_COUNT]", assigned.length, "loading=", loading);
    console.log("[ESTAFETA_FRONT_RENDER_COUNT] assigned=", assigned.length, "loading=", loading);
    console.log("[ESTAFETA_RENDER_ASSIGNED]", {
      build_id: ESTAFETA_BUILD_ID,
      assigned_count: assigned.length,
      assigned_statuses: assigned.map(o => o.status),
      assigned_ids: assigned.map(o => o.id),
      loading,
    });
  });

  // Poll orders every 10 seconds
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  // Read current GPS permission state without triggering a prompt
  useEffect(() => {
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: "geolocation" }).then((result) => {
      setGpsPermission(result.state);
      result.onchange = () => setGpsPermission(result.state);
    }).catch(() => {});
  }, []);

  // GPS tracking — gpsActive only set true after backend confirms receipt
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsActive(false);
      setGpsError("GPS não disponível neste dispositivo");
      return;
    }
    const sendLocation = async (pos) => {
      try {
        await estafetaAxios(token).post(
          `${API}/api/v1/deliverers/app/location?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
        );
        setGpsActive(true);
        setGpsError(null);
        setLastLocationSent(Date.now());
      } catch {
        setGpsActive(false);
        setGpsError("Erro ao enviar localização ao servidor");
      }
    };
    const onGpsError = (err) => {
      setGpsActive(false);
      setGpsPermission("denied");
      setGpsError(
        err.code === 1 ? "Permissão de localização negada. Ative nas definições do navegador."
        : err.code === 2 ? "Localização indisponível no dispositivo"
        : "Tempo limite de localização excedido"
      );
    };
    gpsRef.current = navigator.geolocation.watchPosition(sendLocation, onGpsError, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: 10000,
    });
    return () => {
      if (gpsRef.current !== null)
        navigator.geolocation.clearWatch(gpsRef.current);
    };
  }, [token]);

  // Manual GPS activation — triggers browser permission prompt if not yet decided
  const handleRequestGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => setGpsPermission("granted"),
      (err) => {
        setGpsPermission("denied");
        setGpsError(
          err.code === 1 ? "Permissão de localização negada. Ative nas definições do navegador." : "Localização indisponível"
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders();
    setRefreshing(false);
  };

  const handlePickup = async (orderId) => {
    setActionLoading(orderId);
    try {
      await estafetaAxios(token).post(
        `${API}/api/v1/deliverers/app/pickup/${orderId}`
      );
      toast.success(t("orderPickedUp"));
      fetchOrders();
    } catch {
      toast.error(t("error"));
    }
    setActionLoading(null);
  };

  const handleDeliver = async (orderId) => {
    setActionLoading(orderId);
    try {
      await estafetaAxios(token).post(
        `${API}/api/v1/deliverers/app/deliver/${orderId}`
      );
      toast.success(t("orderDelivered"));
      fetchOrders();
    } catch {
      toast.error(t("error"));
    }
    setActionLoading(null);
  };

  const handleArrived = async (orderId) => {
    setActionLoading(orderId);
    try {
      await estafetaAxios(token).post(
        `${API}/api/v1/deliverers/app/arrived/${orderId}`
      );
      toast.success(t("clientNotified"));
      fetchOrders();
    } catch {
      toast.error(t("error"));
    }
    setActionLoading(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("estafeta_token");
    onLogout();
  };

  return {
    deliverer,
    assigned,
    completed,
    loading,
    actionLoading,
    refreshing,
    gpsActive,
    gpsError,
    gpsPermission,
    lastLocationSent,
    handleRequestGps,
    handleRefresh,
    handlePickup,
    handleDeliver,
    handleArrived,
    handleLogout,
  };
}
